import { supabase } from '@/lib/supabase/client';
import { z } from 'zod';
import type { Tables } from '@/lib/supabase/types';

export type ReplyRow = Tables<'annotation_replies'>;

export const replyBodySchema = z.string().min(1, 'Reply cannot be empty').max(1000);

const PALETTE = ['#2563eb', '#7c3aed', '#059669', '#dc2626', '#d97706', '#0891b2'];

function hashColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffffffff;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function makeInitials(name: string): string {
  return name.trim().split(/\s+/).map(p => p[0] ?? '').join('').slice(0, 2).toUpperCase();
}

type ReplyWithProfile = ReplyRow & {
  profiles: { handle: string; display_name: string | null } | null;
};

export interface DisplayReply {
  id: string;
  bodyMd: string;
  authorHandle: string;
  authorName: string;
  initials: string;
  color: string;
  createdAt: string;
  isOwn: boolean;
}

export async function getRepliesByAnnotation(annotationId: string): Promise<DisplayReply[]> {
  const [{ data, error }, { data: { user } }] = await Promise.all([
    supabase
      .from('annotation_replies')
      .select('*, profiles!author_id(handle, display_name)')
      .eq('annotation_id', annotationId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true }),
    supabase.auth.getUser(),
  ]);

  if (error) throw error;

  const userId = user?.id;
  return ((data ?? []) as unknown as ReplyWithProfile[])
    .filter(r => r.profiles)
    .map(r => {
      const name = r.profiles!.display_name ?? r.profiles!.handle;
      return {
        id: r.id,
        bodyMd: r.body_md,
        authorHandle: r.profiles!.handle,
        authorName: name,
        initials: makeInitials(name),
        color: hashColor(r.author_id),
        createdAt: r.created_at,
        isOwn: r.author_id === userId,
      };
    });
}

export async function createReply(annotationId: string, bodyMd: string): Promise<ReplyRow> {
  replyBodySchema.parse(bodyMd);

  const { data, error } = await supabase
    .from('annotation_replies')
    .insert({ annotation_id: annotationId, body_md: bodyMd })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteReply(replyId: string): Promise<void> {
  const { error } = await supabase
    .from('annotation_replies')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', replyId);

  if (error) throw error;
}
